import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchDashboardData, fetchHotelStatus, fetchBookingSummary } from '../services/api'
import KPICards from '../components/KPICards'
import FilterBar from '../components/FilterBar'
import HotelGrid from '../components/HotelGrid'
import XpertMonitoring from '../components/XpertMonitoring'
import BookingContent from '../components/BookingContent'

const Dashboard = ({ onDataUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview')
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  })

  // Determine if we have an auth token to access protected endpoints
  const AUTH_TOKEN_KEY = import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token'
  const hasToken = !!localStorage.getItem(AUTH_TOKEN_KEY)

  // Fetch dashboard summary data
  const { data: dashboardData, isLoading: isDashboardLoading, dataUpdatedAt: dashboardUpdatedAt } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: fetchDashboardData,
    refetchInterval: 180000, // Auto-refresh every 3 minutes
  })

  // Fetch hotel status data
  const { data: hotelData, isLoading: isHotelLoading, dataUpdatedAt: hotelUpdatedAt } = useQuery({
    queryKey: ['hotel-status'],
    queryFn: fetchHotelStatus,
    refetchInterval: 180000, // Auto-refresh every 3 minutes
  })

  // Fetch booking summary data (only when token exists or bookings tab is active)
  const { data: bookingData, isLoading: isBookingLoading, dataUpdatedAt: bookingUpdatedAt } = useQuery({
    queryKey: ['booking-summary'],
    queryFn: fetchBookingSummary,
    refetchInterval: 60000, // Auto-refresh every 1 minute for more frequent booking updates
    enabled: hasToken || activeTab.startsWith('bookings-'),
  })

  // Update parent component with latest data timestamp
  useEffect(() => {
    if (onDataUpdate && (dashboardUpdatedAt || hotelUpdatedAt || bookingUpdatedAt)) {
      const latestUpdate = Math.max(
        dashboardUpdatedAt || 0, 
        hotelUpdatedAt || 0, 
        bookingUpdatedAt || 0
      )
      onDataUpdate(new Date(latestUpdate))
    }
  }, [dashboardUpdatedAt, hotelUpdatedAt, bookingUpdatedAt, onDataUpdate])

  const isLoading = isDashboardLoading || isHotelLoading || isBookingLoading

  const tabs = [
    { id: 'overview', label: 'Hotel Status Overview', icon: '🏨' },
    { id: 'bookings-today', label: 'Recent Bookings', icon: '📅' },
    // Hidden tabs for now
    // { id: 'bookings-upcoming', label: 'Upcoming Bookings', icon: '⏰' },
    // { id: 'bookings-checkin', label: 'Check-ins Today', icon: '🏨' },
    // { id: 'bookings-checkout', label: 'Check-outs Today', icon: '🚪' },
    { id: 'reservations', label: 'Reservations API', icon: '📋' },
    { id: 'inventory', label: 'Room Inventory API', icon: '🛏️' },
    // { id: 'confirmations', label: 'Confirmations API', icon: '✅' },
  ]

  if (isLoading && activeTab === 'overview') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPI Cards - Now using realtime hotel data with filter support */}
          <KPICards 
            hotelData={hotelData} 
            bookingData={bookingData}
            loading={isHotelLoading || isBookingLoading} 
            filters={filters}
            onFilterChange={setFilters}
          />
          
          {/* Filter Bar */}
          <FilterBar filters={filters} onFilterChange={setFilters} />
          
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 gap-6">
            {/* Hotel Status Grid - Takes full width */}
            <div>
              <HotelGrid 
                data={{ hotels: hotelData || [] }}
                filters={filters}
                loading={isHotelLoading} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Booking Tabs */}
      {(activeTab.startsWith('bookings-')) && (
        <BookingContent activeTab={activeTab} />
      )}

      {(activeTab === 'reservations' || activeTab === 'inventory' || activeTab === 'confirmations') && (
        <XpertMonitoring activeEndpoint={activeTab} />
      )}
    </div>
  )
}

export default Dashboard